import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
// Components
import OrderDetailsTopBar from 'components/orderDetails/OrderDetailsTopBar';
import TasksAccordion from 'components/orderDetails/TasksAccordion';
import OrderMetaBar from 'components/orderDetails/OrderMetaBar';
import LoadingOverlay from 'components/common/LoadingOverlay';
// Actions
import {
  setCartDetails,
  setOrderDetailsAllData,
} from 'store/orderDetails/OrderDetailsActions';
// Selectors
import {
  getOrderDetails,
  getCartDetails,
} from 'store/orderDetails/OrderDetailsSelectors';
import { getIsLoaderOpen } from 'store/app/AppSelectors';
// API
import PreparationTasksService from 'services/PreparationTasksService';
// Helpers
import {
  getTaskErrorMessage,
  getTasksFromError,
  isAssignmentEndedError,
  isStaleTaskError,
  isTasksUnavailable,
} from 'constants/PreparationTasks';

const HFOrderDetailsPage = () => {
  const [hasSubmittedBefore, setHasSubmittedBefore] = useState(false),
    [hasDeviation, setHasDeviation] = useState([]),
    orderDetails = useSelector((state) => getOrderDetails({ state })),
    cartDetails = useSelector((state) => getCartDetails({ state })),
    isLoaderOpen = useSelector((state) => getIsLoaderOpen({ state })),
    deviationEnabled = true,
    params = useParams(),
    dispatch = useDispatch();

  // null = this order runs the current flow (no tasks, or the feature is off for the OPS)
  const [preparationTasks, setPreparationTasks] = useState(null);
  const [busyTaskId, setBusyTaskId] = useState(null);

  const fetchPreparationTasks = useCallback(async () => {
    try {
      const res = await PreparationTasksService.getTasks(params.orderId);
      setPreparationTasks(res?.data ?? null);
    } catch (err) {
      if (isTasksUnavailable(err)) {
        setPreparationTasks(null);
        return;
      }
      toast.error(err?.response?.data?.message ?? 'Could not load the order tasks');
    }
  }, [params.orderId]);

  // Every action answers with the whole list, so one call redraws all the cards.
  const handleTaskAction = async (action, task, options = {}) => {
    const orderId = params.orderId;
    setBusyTaskId(task?.taskId);
    try {
      const calls = {
        start: () => PreparationTasksService.startTask(orderId, task?.taskId),
        pause: () => PreparationTasksService.pauseTask(orderId, task?.taskId, options.pauseReasonId),
        resume: () => PreparationTasksService.resumeTask(orderId, task?.taskId),
        finish: () => PreparationTasksService.finishTask(orderId, task?.taskId),
      };
      const res = await calls[action]();
      setPreparationTasks(res?.data?.aggregate ?? null);
      // Finishing a task can change what the cart rows show.
      if (action === 'finish') {
        refreshCartDetails();
      }
    } catch (err) {
      toast.error(getTaskErrorMessage(err, 'Could not update the task'));
      // The florist lost the order while the screen was open: everything on it is stale.
      if (isAssignmentEndedError(err)) {
        fetchOrderDetails(orderId);
        fetchPreparationTasks();
        return;
      }
      // A stale list is the usual cause. A rejected action already answers with the
      // current list - only ask again when it doesn't.
      if (isStaleTaskError(err)) {
        const latest = getTasksFromError(err);
        if (latest) {
          setPreparationTasks(latest);
        } else {
          fetchPreparationTasks();
        }
      }
    } finally {
      setBusyTaskId(null);
    }
  };

  const fetchOrderDetails = (orderId) => {
    dispatch(setOrderDetailsAllData(orderId));
  };

  // re-reads only the cart rows, so AI QC states refresh without flashing the page loader
  const refreshCartDetails = useCallback(() => {
    dispatch(setCartDetails(params.orderId));
  }, [dispatch, params.orderId]);

  // AI QC polling re-reads cartDetails, so these flags are merged instead of reset:
  // the deviation state they hold is owned by InventoryTable and is not re-derived here.
  const mergeByCartRow = (previous, buildEntry) =>
    cartDetails.map(
      (c) =>
        (Array.isArray(previous) &&
          previous.find((p) => p?.productId === c?.productId && p?.cartId === c?.id)) ||
        buildEntry(c)
    );

  useEffect(() => {
    if (cartDetails && cartDetails?.length > 0) {
      setHasDeviation((prev) =>
        mergeByCartRow(prev, (c) => ({
          productId: c?.productId,
          hasDeviation: false,
          hasEditDeviation: false,
          cartId: c?.id,
        }))
      );
      setHasSubmittedBefore((prev) =>
        mergeByCartRow(prev, (c) => ({
          productId: c?.productId,
          hasSubmittedBefore: false,
          cartId: c?.id,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartDetails]);

  useEffect(() => {
    fetchOrderDetails(params.orderId);
    fetchPreparationTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoaderOpen && <LoadingOverlay />}
      {!isLoaderOpen && (
        <div className="order-details-page">
          <OrderDetailsTopBar
            orderDetails={orderDetails}
            cartDetails={cartDetails}
            hasSubmittedBefore={hasSubmittedBefore}
            hasDeviation={hasDeviation}
            showPause={deviationEnabled}
            preparationTasks={preparationTasks}
            refreshPreparationTasks={fetchPreparationTasks}
          />
          {cartDetails !== '' && cartDetails?.length > 0 && (
            <div className={`orderDetails-cart-wrapper ${deviationEnabled ? '' : 'no-deviation'}`}>
              <OrderMetaBar orderDetails={orderDetails} />
              <TasksAccordion
                cartDetails={cartDetails}
                orderDetails={orderDetails}
                hasSubmittedBefore={hasSubmittedBefore}
                hasDeviation={hasDeviation}
                setHasSubmittedBefore={setHasSubmittedBefore}
                setHasDeviation={setHasDeviation}
                deviationEnabled={deviationEnabled}
                refreshCartDetails={refreshCartDetails}
                preparationTasks={preparationTasks}
                busyTaskId={busyTaskId}
                onTaskAction={handleTaskAction}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default HFOrderDetailsPage;
